// Datum Calculator: convert a point (or a CSV of points) between horizontal/vertical reference
// frames and tidal datums via NOAA VDatum, and cross-check against a nearby CO-OPS station's own
// published datum table. Background: docs/research/tide-tool.md (task/tide-tool) section 4.
import L from "leaflet";
import { createNoaaCoopsClient } from "../tides/providers/noaa-coops.js";
import {
  createVdatumClient,
  VDATUM_REGIONS,
  VDATUM_H_FRAMES,
  VDATUM_V_FRAMES,
  suggestRegion,
  regionRequiredHFrame,
} from "./providers/vdatum.js";

const STATE_KEY = "hydrotools:datum-calculator:v1";
const STATION_RADIUS_KM = 50;

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

function asNum(v, fallback = null) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function fmt(v, digits = 3) {
  return Number.isFinite(v) ? v.toFixed(digits) : "--";
}

function readState() {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const x = raw ? JSON.parse(raw) : {};
    return {
      lat: asNum(x.lat, 37.8063),
      lon: asNum(x.lon, -122.4659),
      region: typeof x.region === "string" ? x.region : "contiguous",
      autoRegion: x.autoRegion !== false,
      s_h_frame: typeof x.s_h_frame === "string" ? x.s_h_frame : "NAD83_2011",
      t_h_frame: typeof x.t_h_frame === "string" ? x.t_h_frame : "NAD83_2011",
      s_v_frame: typeof x.s_v_frame === "string" ? x.s_v_frame : "NAVD88",
      t_v_frame: typeof x.t_v_frame === "string" ? x.t_v_frame : "MLLW",
      s_z: typeof x.s_z === "string" ? x.s_z : "0",
      unit: x.unit === "ft" || x.unit === "us_ft" ? x.unit : "m",
      autoPickMode: !!x.autoPickMode,
      tab: x.tab === "batch" ? "batch" : "point",
    };
  } catch {
    return {
      lat: 37.8063, lon: -122.4659, region: "contiguous", autoRegion: true,
      s_h_frame: "NAD83_2011", t_h_frame: "NAD83_2011", s_v_frame: "NAVD88", t_v_frame: "MLLW",
      s_z: "0", unit: "m", autoPickMode: false, tab: "point",
    };
  }
}

function saveState(state) {
  try { localStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch {}
}

function downloadCsv(filename, rows) {
  const csvCell = (value) => {
    const s = String(value ?? "");
    return /[",\n]/.test(s) ? `"${s.replaceAll("\"", "\"\"")}"` : s;
  };
  const text = rows.map((r) => r.map(csvCell).join(",")).join("\n");
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Flexible CSV parse: looks for lon/lat/z-ish headers, falls back to column order (lon,lat,z).
function parseBatchCsv(text) {
  const lines = String(text || "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return [];
  const splitRow = (line) => line.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
  const first = splitRow(lines[0]);
  const hasHeader = first.some((c) => /[a-z_]/i.test(c));
  const headers = hasHeader ? first.map((h) => h.toLowerCase().replace(/[^a-z0-9]+/g, "_")) : [];
  const body = hasHeader ? lines.slice(1) : lines;
  const pick = (cols, names, idx) => {
    if (!headers.length) return asNum(cols[idx]);
    for (const n of names) {
      const i = headers.indexOf(n);
      if (i >= 0) return asNum(cols[i]);
    }
    return null;
  };
  const out = [];
  body.forEach((line, i) => {
    const cols = splitRow(line);
    if (cols.length < 2) return;
    const lon = pick(cols, ["lon", "longitude", "x"], 0);
    const lat = pick(cols, ["lat", "latitude", "y"], 1);
    const z = pick(cols, ["z", "elevation", "height", "depth"], 2) ?? 0;
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
    out.push({ id: i + 1, lon, lat, z });
  });
  return out;
}

export const datumCalculatorTool = {
  id: "datum-calculator",
  name: "Datum Calculator",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, containerEl) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(containerEl);
  },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.restore?.(data);
  },
};

function createRuntime(app) {
  const noaa = createNoaaCoopsClient();
  const vdatum = createVdatumClient();
  let containerEl = null;
  let state = readState();
  let busy = false;
  let status = "";
  let pointResult = null;
  let stationInfo = { loading: false, station: null, distanceKm: null, datums: null, error: null };
  let batchRows = [];
  let batchResults = null;
  let pickMarker = null;
  let mapClickOff = null;

  function persist() { saveState(state); }
  function setStatus(text) { status = String(text || ""); render(); }

  // See regionRequiredHFrame: NOAA's VDatum server requires a specific horizontal frame for a
  // tidal vertical frame in some regions. Called after anything changes region or a vertical
  // frame, so the UI self-corrects instead of making the user hit NOAA's error.
  function applyRegionFrameFix() {
    state.s_h_frame = regionRequiredHFrame(state.region, state.s_v_frame) || state.s_h_frame;
    state.t_h_frame = regionRequiredHFrame(state.region, state.t_v_frame) || state.t_h_frame;
  }

  function updateMapMarker() {
    const map = app?.map?.instance;
    if (!map || !Number.isFinite(state.lat) || !Number.isFinite(state.lon)) return;
    if (!pickMarker) {
      pickMarker = L.circleMarker([state.lat, state.lon], { radius: 6, color: "#a78bfa", weight: 2, fillColor: "#a78bfa", fillOpacity: 0.25 });
    } else {
      pickMarker.setLatLng([state.lat, state.lon]);
    }
    if (!map.hasLayer(pickMarker)) pickMarker.addTo(map);
    pickMarker.bindTooltip("Datum calculator point", { direction: "right", opacity: 0.9 });
  }

  function setMapClickMode(enabled) {
    const map = app?.map?.instance;
    if (!map) return;
    if (mapClickOff) { try { mapClickOff(); } catch {} mapClickOff = null; }
    state.autoPickMode = !!enabled;
    persist();
    if (!enabled) return;
    const onClick = (e) => {
      state.lat = Number(e.latlng.lat.toFixed(6));
      state.lon = Number(e.latlng.lng.toFixed(6));
      if (state.autoRegion) state.region = suggestRegion(state.lat, state.lon);
      applyRegionFrameFix();
      persist();
      updateMapMarker();
      refreshStationInfo();
      render();
    };
    map.on("click", onClick);
    mapClickOff = () => map.off("click", onClick);
  }

  function setPointFromMapCenter() {
    const center = app?.map?.instance?.getCenter?.();
    if (!center) return;
    state.lat = Number(center.lat.toFixed(6));
    state.lon = Number(center.lng.toFixed(6));
    if (state.autoRegion) state.region = suggestRegion(state.lat, state.lon);
    applyRegionFrameFix();
    persist();
    updateMapMarker();
    refreshStationInfo();
    render();
  }

  function readForm() {
    if (!containerEl) return;
    const q = (id) => containerEl.querySelector(id);
    state.lat = asNum(q("#dc-lat")?.value, state.lat);
    state.lon = asNum(q("#dc-lon")?.value, state.lon);
    state.s_z = String(q("#dc-sz")?.value ?? state.s_z);
    state.region = q("#dc-region")?.value || state.region;
    state.s_h_frame = q("#dc-shframe")?.value || state.s_h_frame;
    state.t_h_frame = q("#dc-thframe")?.value || state.t_h_frame;
    state.s_v_frame = q("#dc-svframe")?.value || state.s_v_frame;
    state.t_v_frame = q("#dc-tvframe")?.value || state.t_v_frame;
    state.unit = q("#dc-unit")?.value || state.unit;
    applyRegionFrameFix();
    persist();
  }

  async function refreshStationInfo() {
    if (!Number.isFinite(state.lat) || !Number.isFinite(state.lon)) return;
    stationInfo = { loading: true, station: null, distanceKm: null, datums: null, error: null };
    render();
    try {
      const hit = await noaa.findNearestStation(state.lat, state.lon);
      if (!hit?.station || hit.distanceKm > STATION_RADIUS_KM) {
        stationInfo = { loading: false, station: null, distanceKm: hit?.distanceKm ?? null, datums: null, error: null };
        render();
        return;
      }
      const { datums, epoch } = await noaa.fetchStationDatums(hit.station.id, state.unit === "m" ? "metric" : "english");
      stationInfo = { loading: false, station: hit.station, distanceKm: hit.distanceKm, datums, epoch, error: null };
    } catch (err) {
      stationInfo = { loading: false, station: null, distanceKm: null, datums: null, error: String(err?.message || err) };
    }
    render();
  }

  async function runPointConvert() {
    readForm();
    if (!Number.isFinite(state.lat) || !Number.isFinite(state.lon)) {
      setStatus("Enter a valid lat/lon.");
      return;
    }
    busy = true;
    setStatus("Converting...");
    try {
      const out = await vdatum.convertPoint({
        s_x: state.lon, s_y: state.lat, s_z: state.s_z || "0",
        region: state.region, s_h_frame: state.s_h_frame, t_h_frame: state.t_h_frame,
        s_v_frame: state.s_v_frame, t_v_frame: state.t_v_frame,
        s_v_unit: state.unit, t_v_unit: state.unit,
      });
      pointResult = out;
      setStatus(out.cached ? "Converted (cached)." : "Converted.");
    } catch (err) {
      pointResult = null;
      setStatus(`Conversion failed: ${err?.message || err}`);
    } finally {
      busy = false;
      render();
    }
  }

  async function onBatchFile(file) {
    if (!file) return;
    const text = await file.text();
    batchRows = parseBatchCsv(text);
    batchResults = null;
    setStatus(batchRows.length ? `Parsed ${batchRows.length} point(s). Ready to convert.` : "No lon/lat rows found in that file.");
  }

  async function runBatchConvert() {
    readForm();
    if (!batchRows.length) { setStatus("Upload a CSV first."); return; }
    if (batchRows.length > 2000) { setStatus("Max 2000 rows per batch."); return; }
    busy = true;
    setStatus(`Converting ${batchRows.length} point(s)...`);
    try {
      batchResults = await vdatum.convertBatch(batchRows, {
        region: state.region, s_h_frame: state.s_h_frame, t_h_frame: state.t_h_frame,
        s_v_frame: state.s_v_frame, t_v_frame: state.t_v_frame,
        s_v_unit: state.unit, t_v_unit: state.unit,
      });
      const failed = batchResults.filter((r) => !r.ok).length;
      setStatus(failed ? `Done, ${failed} of ${batchResults.length} failed.` : `Converted ${batchResults.length} point(s).`);
    } catch (err) {
      setStatus(`Batch conversion failed: ${err?.message || err}`);
    } finally {
      busy = false;
      render();
    }
  }

  function downloadBatchResults() {
    if (!batchResults?.length) return;
    const rows = [["id", "lon", "lat", "z_in", `${state.t_v_frame}_out`, "uncertainty_m", "error"]];
    batchResults.forEach((r, i) => {
      const src = batchRows[i] || {};
      rows.push([r.id, src.lon, src.lat, src.z, r.ok ? fmt(r.t_z, 4) : "", r.ok ? fmt(r.uncertainty, 4) : "", r.ok ? "" : r.error]);
    });
    downloadCsv(`datum-calculator-${state.s_v_frame}-to-${state.t_v_frame}.csv`, rows);
  }

  function frameOptions(current, list) {
    return list.map((f) => `<option value="${esc(f)}"${f === current ? " selected" : ""}>${esc(f)}</option>`).join("");
  }

  function vFrameOptions(current) {
    return VDATUM_V_FRAMES.map((f) => `<option value="${esc(f.code)}"${f.code === current ? " selected" : ""}>${esc(f.label)}</option>`).join("");
  }

  function regionOptions(current) {
    return VDATUM_REGIONS.map((r) => `<option value="${esc(r.code)}"${r.code === current ? " selected" : ""}>${esc(r.label)}</option>`).join("");
  }

  function renderStationCard() {
    if (stationInfo.loading) return `<div class="small">Checking for a nearby CO-OPS station...</div>`;
    if (stationInfo.error) return `<div class="small">Station lookup failed: ${esc(stationInfo.error)}</div>`;
    if (!stationInfo.station) {
      const d = Number.isFinite(stationInfo.distanceKm) ? ` (nearest is ${stationInfo.distanceKm.toFixed(0)} km away)` : "";
      return `<div class="small">No CO-OPS station within ${STATION_RADIUS_KM} km${esc(d)} -- use the VDatum grid result above.</div>`;
    }
    const rows = (stationInfo.datums || []).map((d) => `
      <tr><td>${esc(d.name)}</td><td style="text-align:right;">${fmt(d.value, 3)}</td><td class="small">${esc(d.description)}</td></tr>
    `).join("");
    return `
      <div class="small">
        Station <strong>${esc(stationInfo.station.name)}</strong> (${esc(stationInfo.station.id)}), ${stationInfo.distanceKm.toFixed(1)} km away --
        its own published datums (most authoritative source right at the station; epoch ${esc(stationInfo.epoch || "1983-2001")}):
      </div>
      <table style="width:100%; border-collapse:collapse; margin-top:6px;">
        <thead><tr>
          <th style="text-align:left; border-bottom:1px solid rgba(148,163,184,0.25);">Datum</th>
          <th style="text-align:right; border-bottom:1px solid rgba(148,163,184,0.25);">Value (${esc(state.unit)})</th>
          <th style="text-align:left; border-bottom:1px solid rgba(148,163,184,0.25);">Description</th>
        </tr></thead>
        <tbody>${rows || `<tr><td colspan="3" class="small">No datum table published for this station.</td></tr>`}</tbody>
      </table>
    `;
  }

  function renderPointResult() {
    if (!pointResult) return `<div class="small">No conversion yet.</div>`;
    return `
      <div class="small" style="margin-top:8px;">
        <strong>${fmt(pointResult.t_z, 4)} ${esc(state.unit)}</strong> in ${esc(pointResult.t_v_frame)}
        &plusmn; ${fmt(pointResult.uncertainty, 4)} m uncertainty
      </div>
      <div class="small" style="margin-top:4px; color:var(--muted);">
        Path: ${esc(pointResult.s_h_frame)}/${esc(pointResult.s_v_frame)} &rarr; ${esc(pointResult.t_h_frame)}/${esc(pointResult.t_v_frame)}
        &middot; region ${esc(pointResult.region)} &middot; NOAA VDatum grid, NTDE 1983-2001
      </div>
    `;
  }

  function renderBatchTable() {
    if (!batchResults) {
      return batchRows.length ? `<div class="small">${batchRows.length} point(s) parsed. Click Convert.</div>` : `<div class="small">No file loaded.</div>`;
    }
    const rows = batchResults.slice(0, 200).map((r, i) => {
      const src = batchRows[i] || {};
      return `<tr>
        <td>${esc(r.id)}</td><td>${fmt(src.lon, 5)}</td><td>${fmt(src.lat, 5)}</td>
        <td style="text-align:right;">${r.ok ? fmt(r.t_z, 4) : "--"}</td>
        <td style="text-align:right;">${r.ok ? fmt(r.uncertainty, 4) : "--"}</td>
        <td class="small">${r.ok ? "" : esc(r.error)}</td>
      </tr>`;
    }).join("");
    return `
      <div class="small" style="margin-bottom:6px;">Showing ${Math.min(batchResults.length, 200)} of ${batchResults.length}.</div>
      <div style="overflow:auto; max-height:260px;">
        <table style="width:100%; border-collapse:collapse;">
          <thead><tr>
            <th style="text-align:left;">id</th><th style="text-align:left;">lon</th><th style="text-align:left;">lat</th>
            <th style="text-align:right;">${esc(state.t_v_frame)}</th><th style="text-align:right;">&plusmn;unc (m)</th><th style="text-align:left;">error</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    `;
  }

  function render() {
    if (!containerEl) return;
    const tab = state.tab;
    containerEl.innerHTML = `
      <div class="card" style="padding:10px;">
        <div class="lm-title">Datum Calculator</div>
        <div class="small" style="margin-top:6px;">
          Convert between horizontal/vertical frames and tidal datums via NOAA VDatum, or read a
          nearby CO-OPS station's own published datum table.
        </div>

        <div class="row lm-add-row" style="margin-top:10px;">
          <button type="button" class="btn${tab === "point" ? " primary" : ""}" data-dc-tab="point">Point</button>
          <button type="button" class="btn${tab === "batch" ? " primary" : ""}" data-dc-tab="batch">Batch CSV</button>
        </div>

        <div class="lm-title" style="margin-top:10px;">Location</div>
        <div class="row lm-add-row">
          <label class="small" style="min-width:48px;">Lat</label>
          <input id="dc-lat" type="number" step="0.0001" value="${esc(state.lat)}" style="flex:1;" />
        </div>
        <div class="row lm-add-row" style="margin-top:6px;">
          <label class="small" style="min-width:48px;">Lon</label>
          <input id="dc-lon" type="number" step="0.0001" value="${esc(state.lon)}" style="flex:1;" />
        </div>
        <div class="row lm-add-row" style="margin-top:6px;">
          <button id="dc-map-center" class="btn">Use Map Center</button>
          <label class="small" style="display:flex; align-items:center; gap:6px;">
            <input id="dc-map-pick" type="checkbox" ${state.autoPickMode ? "checked" : ""} />
            Map click pick
          </label>
        </div>

        <div class="lm-title" style="margin-top:10px;">Region &amp; frames</div>
        <div class="row lm-add-row" style="align-items:center;">
          <select id="dc-region" style="flex:1;">${regionOptions(state.region)}</select>
          <label class="small" style="display:flex; align-items:center; gap:6px;">
            <input id="dc-auto-region" type="checkbox" ${state.autoRegion ? "checked" : ""} /> auto
          </label>
        </div>
        <div class="row lm-add-row" style="margin-top:6px;">
          <label class="small" style="min-width:70px;">From</label>
          <select id="dc-shframe" style="flex:1;">${frameOptions(state.s_h_frame, VDATUM_H_FRAMES)}</select>
          <select id="dc-svframe" style="flex:1;">${vFrameOptions(state.s_v_frame)}</select>
        </div>
        <div class="row lm-add-row" style="margin-top:6px;">
          <label class="small" style="min-width:70px;">To</label>
          <select id="dc-thframe" style="flex:1;">${frameOptions(state.t_h_frame, VDATUM_H_FRAMES)}</select>
          <select id="dc-tvframe" style="flex:1;">${vFrameOptions(state.t_v_frame)}</select>
        </div>
        <div class="row lm-add-row" style="margin-top:6px;">
          <label class="small" style="min-width:70px;">Units</label>
          <select id="dc-unit" style="flex:1;">
            <option value="m"${state.unit === "m" ? " selected" : ""}>meters</option>
            <option value="ft"${state.unit === "ft" ? " selected" : ""}>feet (int'l)</option>
            <option value="us_ft"${state.unit === "us_ft" ? " selected" : ""}>feet (US survey)</option>
          </select>
        </div>

        ${tab === "point" ? `
          <div class="row lm-add-row" style="margin-top:6px;">
            <label class="small" style="min-width:70px;">Source Z</label>
            <input id="dc-sz" type="number" step="any" value="${esc(state.s_z)}" style="flex:1;" />
            <button id="dc-convert" class="btn primary" ${busy ? "disabled" : ""}>Convert</button>
          </div>
          <div id="dc-point-result">${renderPointResult()}</div>

          <div class="lm-title" style="margin-top:12px;">Nearby station check</div>
          <div id="dc-station-card">${renderStationCard()}</div>
        ` : `
          <div class="lm-title" style="margin-top:10px;">CSV of points</div>
          <div class="small" style="margin-bottom:6px;">Columns: lon, lat, z (header optional; same source/target settings applied to every row, max 2000 rows).</div>
          <input id="dc-batch-file" type="file" accept=".csv,.txt" style="width:100%;" />
          <div class="row lm-add-row" style="margin-top:6px;">
            <button id="dc-batch-convert" class="btn primary" ${busy ? "disabled" : ""}>Convert all</button>
            <button id="dc-batch-download" class="btn" ${batchResults?.length ? "" : "disabled"}>Download CSV</button>
          </div>
          <div id="dc-batch-table" style="margin-top:8px;">${renderBatchTable()}</div>
        `}

        <div class="small" style="margin-top:10px; color:var(--muted);">${esc(status)}</div>
      </div>
    `;

    containerEl.querySelector("#dc-map-center")?.addEventListener("click", setPointFromMapCenter);
    containerEl.querySelector("#dc-map-pick")?.addEventListener("change", (e) => setMapClickMode(e.target.checked));
    containerEl.querySelector("#dc-auto-region")?.addEventListener("change", (e) => {
      state.autoRegion = e.target.checked;
      if (state.autoRegion) state.region = suggestRegion(state.lat, state.lon);
      applyRegionFrameFix();
      persist();
      render();
    });
    containerEl.querySelectorAll("[data-dc-tab]").forEach((btn) => {
      btn.addEventListener("click", () => { state.tab = btn.dataset.dcTab; persist(); render(); });
    });
    containerEl.querySelector("#dc-convert")?.addEventListener("click", runPointConvert);
    containerEl.querySelector("#dc-batch-file")?.addEventListener("change", (e) => onBatchFile(e.target.files?.[0]));
    containerEl.querySelector("#dc-batch-convert")?.addEventListener("click", runBatchConvert);
    containerEl.querySelector("#dc-batch-download")?.addEventListener("click", downloadBatchResults);
    ["#dc-lat", "#dc-lon", "#dc-sz", "#dc-region", "#dc-shframe", "#dc-thframe", "#dc-svframe", "#dc-tvframe", "#dc-unit"].forEach((sel) => {
      containerEl.querySelector(sel)?.addEventListener("change", readForm);
    });

    updateMapMarker();
  }

  function mount(el) {
    containerEl = el;
    render();
    if (state.autoPickMode) setMapClickMode(true);
    refreshStationInfo();
  }

  function destroy() {
    if (mapClickOff) { try { mapClickOff(); } catch {} mapClickOff = null; }
    const map = app?.map?.instance;
    if (map && pickMarker && map.hasLayer(pickMarker)) map.removeLayer(pickMarker);
    containerEl = null;
  }

  function snapshot() { return { state }; }
  function restore(data) {
    if (data?.state) state = { ...state, ...data.state };
    render();
  }

  return { mount, destroy, snapshot, restore };
}
